import {
  DeleteMyFleetVesselsResponse,
  MyFleetVesselDeleteRequest,
} from '../models/my-fleet.model';
import { chunkArray, runWithConcurrency } from '../utils/batchHelpers';
import { deleteMyFleetVessels } from './my-fleet';

/**
 * Batch delete vessels with progress and error collection.
 *
 * @param vesselIds - Array of vessel IDs to delete.
 * @param batchSize - Batch size for vessel deletion.
 * @param concurrency - Concurrency limit for vessel deletion.
 * @param setProgress - Optional callback for progress updates: (completed, total, failed) => void
 * @param setErrors - Optional callback for batch errors.
 * @param setResponse - Optional callback for batch responses.
 * @returns {Promise<{ deleted: string[], errors: any[] }>} Results and errors.
 */
interface BatchDeleteVesselsOptions {
  vesselImos: string[];
  batchSize: number;
  concurrency: number;
  setProgress?: (progress: {
    completed: number;
    total: number;
    failed: number;
  }) => void;
  setErrors?: (errors: any[]) => void;
  setResponse?: (response: DeleteMyFleetVesselsResponse) => void;
}

async function batchDeleteVessels({
  vesselImos,
  batchSize,
  concurrency,
  setProgress,
  setErrors,
  setResponse,
}: BatchDeleteVesselsOptions): Promise<{ deleted: string[]; errors: any[] }> {
  const vesselBatches: string[][] = chunkArray(vesselImos, batchSize);
  let completed = 0;
  let failed = 0;
  let errors: any[] = [];
  let deleted: string[] = [];

  const tasks = vesselBatches.map((batch) => async () => {
    if (setProgress)
      setProgress({ completed, total: vesselImos.length, failed });
    const payload: MyFleetVesselDeleteRequest = { imos: batch };
    try {
      const resp = await deleteMyFleetVessels(payload);

      if (Array.isArray(resp)) {
        errors = [...errors, ...resp];
        if (setErrors) setErrors(errors);
      } else if (typeof resp === 'string') {
        deleted = [...deleted, ...batch];
      }
      completed += batch.length;
      if (setProgress)
        setProgress({ completed, total: vesselImos.length, failed });
      if (setResponse) setResponse(resp);
      return resp;
    } catch (error: any) {
      failed += batch.length;
      if (setProgress)
        setProgress({ completed, total: vesselImos.length, failed });
      const errList = error?.response?.data?.errors
        ? error.response.data.errors
        : [{ message: error?.message || 'Batch vessel deletion failed.' }];
      errors = [...errors, ...errList];
      if (setErrors) setErrors(errors);
      return null;
    }
  });

  await runWithConcurrency(tasks, concurrency);

  return { deleted, errors };
}

export default batchDeleteVessels;
